/**
 * Loads a presentation deck's sheets.
 *
 * Each file under a `content/<deck>/` folder becomes its own lazy chunk, mirroring flat docs: the
 * route downloads only the sheet being shown, and neighbors can be prefetched from here.
 */
const loaders = import.meta.glob("../../content/*/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

const byKey = new Map(
  Object.entries(loaders).map(([filePath, load]) => {
    const parts = filePath.split("/");

    return [`${parts.at(-2)}/${parts.at(-1)?.replace(/\.md$/i, "")}`, load];
  }),
);

const FRONTMATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;

/** Markdown body of one sheet (`"deck"` is the cover), with the frontmatter block stripped. */
export const loadDeckSheet = async (deck: string, file: string): Promise<string> => {
  const load = byKey.get(`${deck}/${file}`);

  if (!load) throw new Error(`No content file for sheet "${deck}/${file}"`);

  return (await load()).replace(FRONTMATTER, "");
};

/** Warms a neighboring sheet's chunk so the next transition has nothing left to wait for. */
export const prefetchDeckSheet = (deck: string, file: string): void => {
  void byKey
    .get(`${deck}/${file}`)?.()
    .catch(() => {});
};
